import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", href);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#080f23]/90 backdrop-blur-xl border-b border-white/[0.08]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16">
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <div className="w-8 h-8 rounded-lg bg-[#38BDF8] flex items-center justify-center shadow-[0_0_16px_rgba(56,189,248,0.4)]">
            <span className="text-[#080f23] font-black text-sm">T</span>
          </div>
          <span className="font-semibold text-white text-[1.05rem]">
            Tyo <span className="text-[#38BDF8]">Solution</span>
          </span>
        </button>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="text-sm text-[#94A3B8] hover:text-white transition-colors duration-200 font-medium"
              data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => scrollTo("#contact")}
            className="text-sm text-[#94A3B8] hover:text-white transition-colors border border-white/[0.12] hover:border-white/25 px-4 py-2 rounded-lg font-medium"
            data-testid="nav-cta-contact"
          >
            Contact Us
          </button>
          <a
            href="https://ocr.tyosolution.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#38BDF8] text-[#080f23] font-semibold text-sm px-5 py-2 rounded-lg hover:bg-[#0EA5E9] transition-colors flex items-center gap-1.5 shadow-[0_0_20px_rgba(56,189,248,0.3)]"
            data-testid="nav-cta-demo"
          >
            Try OCR Free
          </a>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white" data-testid="nav-mobile-toggle">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#080f23]/95 backdrop-blur-xl border-b border-white/[0.08] px-6 py-5 flex flex-col gap-3">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="text-left text-sm text-[#94A3B8] hover:text-white py-2.5 border-b border-white/[0.06] font-medium"
            >
              {link.label}
            </button>
          ))}
          <a
            href="https://ocr.tyosolution.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#38BDF8] text-[#080f23] font-semibold text-sm px-5 py-3 rounded-lg hover:bg-[#0EA5E9] transition-colors mt-2 flex items-center justify-center gap-2"
          >
            Try OCR Free
          </a>
        </div>
      )}
    </nav>
  );
}
